// src/pages/members/MemberAdd.jsx
import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {get, push, ref, set} from 'firebase/database';
import {database} from '../../config/firebase';
import {useAuth} from '../../hooks/useAuth';
import {encodeEmail} from "../../utils/utils";

// Liste des professions (la même que dans le formulaire de recensement)
const PROFESSIONS = [
    "Élève",
    "Étudiant",
    "Enseignant",
    "Ingénieur",
    "Médecin",
    "Infirmier(ère)",
    "Commerçant(e)",
    "Entrepreneur",
    "Comptable",
    "Banquier",
    "Fonctionnaire",
    "Agent de sécurité",
    "Militaire",
    "Avocat",
    "Agriculteur",
    "Artisan",
    "Informaticien",
    "Marketing/Communication",
    "Artiste",
    "Couturier(ère)",
    "Chef d'entreprise",
    "Sans emploi",
    "Retraité",
    "Autre"
];

const MemberAdd = () => {
    const [formData, setFormData] = useState({
        prenom: '',
        nom: '',
        email: '',
        telephone: '',
        sexe: '',
        baptise: false,
        profession: '',
        churchId: ''
    });

    const [churches, setChurches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [churchesLoading, setChurchesLoading] = useState(true);
    const [searchProfession, setSearchProfession] = useState('');
    const [showProfessionDropdown, setShowProfessionDropdown] = useState(false);

    const {userRole} = useAuth();
    const navigate = useNavigate();

    // Filtrer les professions en fonction de la recherche
    const filteredProfessions = PROFESSIONS.filter(profession =>
        profession.toLowerCase().includes(searchProfession.toLowerCase())
    );

    // Charger la liste des églises
    useEffect(() => {
        const fetchChurches = async () => {
            try {
                setChurchesLoading(true);

                // Si l'utilisateur est admin d'église, récupérer seulement son église
                if (userRole?.role === 'church_admin' && userRole?.churchId) {
                    const churchRef = ref(database, `churches/${userRole.churchId}`);
                    const snapshot = await get(churchRef);

                    if (snapshot.exists()) {
                        const church = {
                            id: userRole.churchId,
                            ...snapshot.val()
                        };

                        setChurches([church]);
                        setFormData(prev => ({...prev, churchId: church.id}));
                    }
                } else {
                    // Sinon, récupérer toutes les églises
                    const churchesRef = ref(database, 'churches');
                    const snapshot = await get(churchesRef);

                    if (snapshot.exists()) {
                        const churchesList = [];
                        snapshot.forEach((childSnapshot) => {
                            churchesList.push({
                                id: childSnapshot.key,
                                ...childSnapshot.val()
                            });
                        });

                        // Trier les églises par nom
                        churchesList.sort((a, b) => a.nom.localeCompare(b.nom));

                        setChurches(churchesList);

                        // Sélectionner la première église par défaut
                        if (churchesList.length > 0) {
                            setFormData(prev => ({...prev, churchId: churchesList[0].id}));
                        }
                    }
                }
            } catch (error) {
                console.error("Erreur lors du chargement des églises:", error);
                setError("Impossible de charger la liste des églises. Veuillez réessayer plus tard.");
            } finally {
                setChurchesLoading(false);
            }
        };

        fetchChurches();
    }, [userRole]);

    // Gérer le clic en dehors du dropdown des professions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showProfessionDropdown && !event.target.closest('.profession-dropdown')) {
                setShowProfessionDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showProfessionDropdown]);

    // Gérer les changements dans le formulaire
    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Gérer la sélection d'une profession
    const handleProfessionSelect = (profession) => {
        setFormData(prev => ({
            ...prev,
            profession
        }));
        setSearchProfession('');
        setShowProfessionDropdown(false);
    };

    // Soumettre le formulaire
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Vérifier que tous les champs obligatoires sont remplis
            const requiredFields = ['prenom', 'nom', 'telephone', 'sexe', 'profession', 'churchId'];
            for (const field of requiredFields) {
                if (!formData[field]) {
                    throw new Error(`Le champ ${field} est obligatoire.`);
                }
            }

            // Vérifier que le téléphone a un format valide
            if (!/^\+?[0-9]{8,}$/.test(formData.telephone)) {
                throw new Error("Numéro de téléphone invalide. Veuillez utiliser un format international (ex: +22966666666).");
            }

            // Vérifier si l'email a un format valide (si fourni)
            if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                throw new Error("Adresse email invalide.");
            }

            // Vérifier si le numéro de téléphone existe déjà
            const phoneRef = ref(database, `userLookup/byPhone/${formData.telephone}`);
            const phoneSnapshot = await get(phoneRef);

            if (phoneSnapshot.exists()) {
                throw new Error("Ce numéro de téléphone est déjà enregistré. Si cette personne est déjà recensée, veuillez vérifier dans la liste des membres.");
            }

            // Vérifier si l'email existe déjà (si fourni)
            if (formData.email) {
                // Encoder l'email pour éviter les caractères invalides dans le chemin Firebase
                const encodedEmail = encodeEmail(formData.email);
                const emailRef = ref(database, `userLookup/byEmail/${encodedEmail}`);
                const emailSnapshot = await get(emailRef);

                if (emailSnapshot.exists()) {
                    throw new Error("Cette adresse email est déjà enregistrée. Si cette personne est déjà recensée, veuillez vérifier dans la liste des membres.");
                }
            }
            // Créer un nouvel enregistrement de membre
            const membersRef = ref(database, 'users');
            const newMemberRef = push(membersRef);
            const uid = newMemberRef.key;

            // Préparer les données du membre
            const memberData = {
                profile: {
                    prenom: formData.prenom,
                    nom: formData.nom,
                    email: formData.email || '',
                    telephone: formData.telephone,
                    sexe: formData.sexe,
                    baptise: formData.baptise,
                    profession: formData.profession,
                    churchId: formData.churchId,
                    createdAt: Date.now(),
                    updatedAt: Date.now()
                }
            };

            // Enregistrer le membre
            await set(newMemberRef, memberData);

            // Créer les entrées dans les tables de référence
            if (formData.email) {
                // Encoder l'email pour éviter les caractères invalides dans le chemin Firebase
                const encodedEmail = encodeEmail(formData.email);
                await set(ref(database, `userLookup/byEmail/${encodedEmail}`), uid);
            }
            await set(ref(database, `userLookup/byPhone/${formData.telephone}`), uid);

            // Ajouter le membre à la liste des membres de l'église
            await set(ref(database, `churchMembers/${formData.churchId}/${uid}`), true);

            // Mettre à jour les statistiques
            await updateStatistics(formData);

            // Rediriger vers la liste des membres
            navigate('/members');
        } catch (error) {
            console.error("Erreur lors de l'ajout du membre:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Mettre à jour les statistiques
    const updateStatistics = async (userData) => {
        try {
            // Mettre à jour les statistiques de l'église
            const churchStatsRef = ref(database, `statistics/churches/${userData.churchId}`);
            const churchStatsSnapshot = await get(churchStatsRef);

            if (churchStatsSnapshot.exists()) {
                const churchStats = churchStatsSnapshot.val();
                await set(churchStatsRef, {
                    ...churchStats,
                    totalMembres: (churchStats.totalMembres || 0) + 1,
                    hommes: userData.sexe === 'M' ? (churchStats.hommes || 0) + 1 : (churchStats.hommes || 0),
                    femmes: userData.sexe === 'F' ? (churchStats.femmes || 0) + 1 : (churchStats.femmes || 0),
                    baptises: userData.baptise ? (churchStats.baptises || 0) + 1 : (churchStats.baptises || 0)
                });
            } else {
                await set(churchStatsRef, {
                    totalMembres: 1,
                    hommes: userData.sexe === 'M' ? 1 : 0,
                    femmes: userData.sexe === 'F' ? 1 : 0,
                    baptises: userData.baptise ? 1 : 0
                });
            }

            // Mettre à jour les statistiques globales
            const globalStatsRef = ref(database, `statistics/global`);
            const globalStatsSnapshot = await get(globalStatsRef);

            if (globalStatsSnapshot.exists()) {
                const globalStats = globalStatsSnapshot.val();
                await set(globalStatsRef, {
                    ...globalStats,
                    totalMembres: (globalStats.totalMembres || 0) + 1,
                    hommes: userData.sexe === 'M' ? (globalStats.hommes || 0) + 1 : (globalStats.hommes || 0),
                    femmes: userData.sexe === 'F' ? (globalStats.femmes || 0) + 1 : (globalStats.femmes || 0),
                    baptises: userData.baptise ? (globalStats.baptises || 0) + 1 : (globalStats.baptises || 0)
                });
            } else {
                await set(globalStatsRef, {
                    totalMembres: 1,
                    hommes: userData.sexe === 'M' ? 1 : 0,
                    femmes: userData.sexe === 'F' ? 1 : 0,
                    baptises: userData.baptise ? 1 : 0
                });
            }

            // Mettre à jour les statistiques par profession
            const professionRef = ref(database, `statistics/professions/${userData.profession}`);
            const professionSnapshot = await get(professionRef);

            if (professionSnapshot.exists()) {
                await set(professionRef, professionSnapshot.val() + 1);
            } else {
                await set(professionRef, 1);
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour des statistiques:", error);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <div className="flex items-center space-x-4">
                    <Link to="/dashboard"
                          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        <svg className="-ml-1 mr-1 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd"
                                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                  clipRule="evenodd"/>
                        </svg>
                        Tableau de bord
                    </Link>
                    <span className="text-gray-500">/</span>
                    <Link to="/members"
                          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        Membres
                    </Link>
                </div>
                <h1 className="mt-4 text-2xl font-bold text-gray-900">Ajouter un nouveau membre</h1>
                <p className="mt-2 text-sm text-gray-700">
                    Remplissez le formulaire ci-dessous pour ajouter un nouveau membre au système.
                </p>
            </div>

            {error && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p>{error}</p>
                </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-8">
                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900 border-b border-gray-200 pb-2">
                                Informations personnelles
                            </h3>
                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="prenom" className="block text-sm font-medium text-gray-700">
                                        Prénom <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="text"
                                            id="prenom"
                                            name="prenom"
                                            value={formData.prenom}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700">
                                        Nom <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="text"
                                            id="nom"
                                            name="nom"
                                            value={formData.nom}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="sexe" className="block text-sm font-medium text-gray-700">
                                        Sexe <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <select
                                            id="sexe"
                                            name="sexe"
                                            value={formData.sexe}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        >
                                            <option value="">Sélectionnez</option>
                                            <option value="M">Homme</option>
                                            <option value="F">Femme</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center h-full pt-6">
                                    <div className="flex items-center h-5">
                                        <input
                                            id="baptise"
                                            name="baptise"
                                            type="checkbox"
                                            checked={formData.baptise}
                                            onChange={handleChange}
                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="baptise" className="font-medium text-gray-700">Cette personne
                                            est baptisée</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900 border-b border-gray-200 pb-2">
                                Coordonnées
                            </h3>
                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700">
                                        Numéro de téléphone <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="tel"
                                            id="telephone"
                                            name="telephone"
                                            value={formData.telephone}
                                            onChange={handleChange}
                                            placeholder="+22966666666"
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        />
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">Format international (ex:
                                        +22966666666)</p>
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                        Adresse e-mail (facultatif)
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900 border-b border-gray-200 pb-2">
                                Informations supplémentaires
                            </h3>
                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                                <div className="relative profession-dropdown">
                                    <label htmlFor="profession" className="block text-sm font-medium text-gray-700">
                                        Profession/Occupation <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                id="profession"
                                                value={formData.profession || searchProfession}
                                                onChange={(e) => {
                                                    setSearchProfession(e.target.value);
                                                    if (!showProfessionDropdown) {
                                                        setShowProfessionDropdown(true);
                                                    }
                                                }}
                                                onClick={() => setShowProfessionDropdown(true)}
                                                placeholder="Sélectionnez ou tapez pour filtrer"
                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                                onClick={() => setShowProfessionDropdown(!showProfessionDropdown)}
                                            >
                                                <svg className="h-5 w-5 text-gray-400"
                                                     xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                                     fill="currentColor">
                                                    <path fillRule="evenodd"
                                                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                          clipRule="evenodd"/>
                                                </svg>
                                            </button>
                                        </div>
                                        {showProfessionDropdown && (
                                            <div
                                                className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                                {filteredProfessions.length > 0 ? (
                                                    filteredProfessions.map((profession) => (
                                                        <div
                                                            key={profession}
                                                            onClick={() => handleProfessionSelect(profession)}
                                                            className="cursor-pointer hover:bg-indigo-500 hover:text-white px-4 py-2"
                                                        >
                                                            {profession}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-gray-500 px-4 py-2">Aucun résultat trouvé</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="churchId" className="block text-sm font-medium text-gray-700">
                                        Église d'appartenance <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <select
                                            id="churchId"
                                            name="churchId"
                                            value={formData.churchId}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                            disabled={userRole?.role === 'church_admin' || churchesLoading}
                                        >
                                            <option value="">Sélectionnez une église</option>
                                            {churches.map(church => (
                                                <option key={church.id} value={church.id}>
                                                    {church.nom}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-5 flex justify-end space-x-3">
                            <Link
                                to="/members"
                                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Annuler
                            </Link>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {loading ? (
                                    <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg"
                         fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                              strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enregistrement...
                  </span>
                                ) : 'Ajouter le membre'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MemberAdd;
